import { zodResolver } from "@hookform/resolvers/zod";
import successLotie from "assets/lotties/success.json";
import theme from "assets/themes";
import { Button, Icon } from "components";
import { CardStep } from "components/atoms/CardStep";
import { FirstStep } from "components/layouts/formFirstStep";
import { FormLeadScoring } from "components/layouts/formLeadScoring";
import CompanyPixel from "components/molecules/CompanyPixel/CompanyPixel";
import { ParagonIntegrations } from "components/molecules/ParagonIntegration";
import Lottie from "lottie-react";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getParagonAuthToken } from "store/slices/crm.slice";
import { createClientICP, updateClientICP } from "store/slices/icp.slice";
import { setCurrentPage } from "store/slices/ui.slice";
import EntryFormatter from "utils/EntryFormatter";
import { schnema } from "./schema";
import { ClientWebhook } from "components/molecules/ClientWebhook";
import { ClayWebhook } from "components/molecules/CrmContainer";
import { getPersonalInfo } from "store/slices/user.slice";
const MAX_STEPS = 5

export const OnBoard = () => {
    const dispatch = useAppDispatch();
    const { data: user } = useAppSelector((state) => state.user);
    const { isLoading: authIsLoading } = useAppSelector((state) => state.crm.paragonAuth);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const form = useForm<any>({
        defaultValues: {
            businessType: 'B2B',
            activeStep: 2,
            icp: 20,
            behavior: 20,
            rows: [],
            state: {
                label: "Nation Wide",
                value: [
                    {
                        label: "Person Location",
                        value: "nation_wide"
                    }
                ]
            }
        },
        resolver: zodResolver(schnema),
    })
    const handleSave = useCallback(() => {
        setIsLoading(true)
        let data = []
        const formData = form.watch()
        if (formData.businessType === 'B2C') {

            let stateValues = Array.isArray(formData.state?.value)
                ? formData.state.value.map((v: any) => v.value || v)
                : formData.state?.value;
            if (Array.isArray(formData.state)) {
                stateValues = formData.state.map((v: any) => v.value || v)
            }
            data = [{
                name: 'Company Location',
                value: stateValues,
                weight: 10,
            }];
        } else {
            data = formData?.rows?.map((item: any) => ({
                name: item.name.value,
                value: Array.isArray(item?.value)
                    ? item?.value.map((v: any) => v?.value)
                    : item?.value,
                weight: item.weight,
            }))
        }
        const dataToSubmit = {
            filters: data,
            thresholdBehavior: 20,
            thresholdIcp: 20,
            businessType: formData.businessType,
        }
        dispatch(createClientICP(dataToSubmit))
            .unwrap()
            .then(() => {
                setIsSuccess(true)
            }).finally(() => {
                setIsLoading(false)
            })
    }, [form.watch()])
    useEffect(() => {
        dispatch(getPersonalInfo());
    }, []);
    useEffect(() => {
        form.setValue('activeStep', 2)
    }, [])
    useEffect(() => {
        window.analytics.page("Onboarding")
        dispatch(setCurrentPage("onboarding"));
        if (EntryFormatter.isObjectEmpty(user) || authIsLoading) return;
        try {
            dispatch(getParagonAuthToken())
        } catch (error) { console.error(error); }
    }, [user]);


    return <div className={`bg-cover bg-primary flex flex-col p-0 bg h-screen items-center justify-start bg-center overflow-y-auto px-5`}>
        <div className="flex w-full justify-center items-center p-4">
            <img src={theme["logo"]} alt="logo" width={180} />
        </div>
        {(isSuccess && !isLoading) ?
            <div className="flex flex-col items-center justify-center gap-2 h-fit w-full m-auto">
                <Lottie animationData={successLotie} loop={false} width={40} className="max-w-[200px]" />
                <h1 className="font-bold capitalize text-2xl">Onboarding Complete</h1>
                <small className="text-gray-400 text-lg">You are all set to start using Identity Matrix AI</small>
                <Link to="/dashboard" className=" mt-4">
                    <Button size="sm" action="button" buttonContainerClassName="gap-4 flex-row-reverse"
                        iconName="arrow-right" iconSize={16} type="primary">
                        Go to Dashboard
                    </Button>
                </Link>
            </div>
            :
            <FormProvider {...form}>
                <div className="flex flex-1 flex-col w-full items-center gap-4">
                    <div className="flex flex-col gap-1 text-center w-full justify-center items-center mt-8">
                        <h1 className="font-bold capitalize text-2xl">Welcome to Identity Matrix AI</h1>
                        <small className="text-white-500">Tell us about your business to complete onboarding.</small>
                    </div>
                    <CardStep step={1} title="Select your business type" description={`Step 1/${MAX_STEPS}`} icon="company">
                        <FirstStep />
                    </CardStep>
                    <CardStep step={2} title={form.watch('businessType') === 'B2B' ? "Add yours ICP's" : "Where are your ideal clients"} description={`Step 2/${MAX_STEPS}`} icon="score" >
                        <FormLeadScoring />
                    </CardStep>
                    <CardStep step={3} title="Where Should We Send Your Hot Leads?" description={`Step 3/${MAX_STEPS}`} icon="layers" >
                        <>
                            <ParagonIntegrations changeLoading />
                            <div className="w-full flex justify-end items-end gap-4 mt-4">
                                <Button size="sm" action="button" buttonContainerClassName="gap-4"
                                    onClick={() => form.setValue('activeStep', 2)}
                                    iconName="arrow-left" iconSize={16} type="outline">
                                    Back
                                </Button>
                                <Button size="sm" action="submit" iconName="arrow-right"
                                    buttonContainerClassName="flex-row-reverse gap-4" disabled={!form.formState.isValid}
                                    onClick={() => form.setValue('activeStep', 4)}
                                >Skip</Button>
                            </div >
                        </>
                    </CardStep>
                    <CardStep step={4} title="Setup your webhook?" description={`Step 3/${MAX_STEPS}`} icon="url" >
                        <>
                            <div className="flex flex-col gap-4 divide-y divide-gray-700">
                                <ClayWebhook showSmall />
                                <ClientWebhook showSmall />
                            </div>
                            <div className="w-full flex justify-end items-end gap-4 mt-4">
                                <Button size="sm" action="button" buttonContainerClassName="gap-4"
                                    onClick={() => form.setValue('activeStep', 3)}
                                    iconName="arrow-left" iconSize={16} type="outline">
                                    Back
                                </Button>
                                <Button size="sm" action="submit" iconName="arrow-right"
                                    buttonContainerClassName="flex-row-reverse gap-4" disabled={!form.formState.isValid}
                                    onClick={() => form.setValue('activeStep', 5)}
                                >Skip</Button>
                            </div>
                        </>
                    </CardStep>
                    <CardStep step={5} title="Add your tracking pixel code" description={`Step 4/${MAX_STEPS}`} icon="code" >
                        <div className="flex flex-col items-center justify-center gap-2 h-fit w-full m-auto">
                            <CompanyPixel />
                            <div className="flex flex-col md:flex-row w-full justify-between">
                                <div className="flex !text-gray-400 gap-2 w-full items-center">
                                    <Icon name="info" size={18} />
                                    <small className="md:whitespace-nowrap" >Add this code on your site, we will use this to track your leads</small>
                                </div>
                                <div className="w-full flex  justify-end items-end gap-4 ">
                                    <Button size="sm" action="button" buttonContainerClassName="gap-4"
                                        onClick={() => form.setValue('activeStep', 4)}
                                        iconName="arrow-left" iconSize={16} type="outline">
                                        Back
                                    </Button>
                                    <Button size="sm" action="submit" iconName="save"
                                        buttonContainerClassName="gap-4" disabled={!form.formState.isValid}
                                        isProcessing={isLoading}
                                        onClick={handleSave}
                                    >Finsh</Button>
                                </div >
                            </div>
                        </div>
                    </CardStep>
                </div>
            </FormProvider>}
    </div>
}